import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Search, LogOut } from 'lucide-react'
import { flattenNavigation, type MenuProps, type FlatNavItem } from '../../core/navigation'
import clsx from 'clsx'

const RECENT_KEY = 'erp_recent_nav'
const MAX_RECENT = 10

function getRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')
  } catch { return [] }
}

function addRecent(href: string) {
  const recent = getRecent().filter(h => h !== href)
  recent.unshift(href)
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)))
}

function fuzzyMatch(query: string, text: string): number {
  const q = query.toLowerCase()
  const t = text.toLowerCase()
  if (t === q) return 200
  if (t.startsWith(q)) return 150
  if (t.includes(q)) return 100
  let score = 0, qi = 0
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) { score += 10; qi++ }
  }
  return qi === q.length ? score : 0
}

export default function CommandPaletteMenu({ navigation, user, onLogout }: MenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const location = useLocation()

  const flatItems = useMemo(() => flattenNavigation(navigation), [navigation])

  const filteredItems = useMemo(() => {
    if (!query.trim()) {
      const recent = getRecent()
      const recentItems = recent
        .map(href => flatItems.find(item => item.href === href))
        .filter(Boolean) as FlatNavItem[]

      const otherItems = flatItems.filter(item => !recent.includes(item.href))

      // Raggruppa per categoria
      const groups: { label: string; items: FlatNavItem[] }[] = []
      if (recentItems.length > 0) {
        groups.push({ label: 'Recenti', items: recentItems })
      }
      const byCategory = new Map<string, FlatNavItem[]>()
      for (const item of otherItems) {
        const cat = item.category || 'Navigazione'
        if (!byCategory.has(cat)) byCategory.set(cat, [])
        byCategory.get(cat)!.push(item)
      }
      byCategory.forEach((items, label) => groups.push({ label, items }))
      return groups
    }

    const scored = flatItems
      .map(item => {
        const nameScore = fuzzyMatch(query, item.name)
        const catScore = item.category ? fuzzyMatch(query, item.category) * 0.5 : 0
        return { item, score: Math.max(nameScore, catScore) }
      })
      .filter(r => r.score > 0)
      .sort((a, b) => b.score - a.score)

    if (scored.length === 0) return []
    return [{ label: 'Risultati', items: scored.map(r => r.item) }]
  }, [query, flatItems])

  const allItems = useMemo(() => {
    return filteredItems.flatMap(g => g.items)
  }, [filteredItems])

  // Ctrl+K globale
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'k') {
        e.preventDefault()
        setIsOpen(prev => !prev)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Focus input quando si apre
  useEffect(() => {
    if (isOpen) {
      setQuery('')
      setSelectedIndex(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [isOpen])

  const navigateTo = useCallback((href: string) => {
    addRecent(href)
    navigate(href)
    setIsOpen(false)
  }, [navigate])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(prev => Math.min(prev + 1, allItems.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(prev => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (allItems[selectedIndex]) {
        navigateTo(allItems[selectedIndex].href)
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }, [allItems, selectedIndex, navigateTo])

  // Scroll selected into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  // Reset selected quando cambia la query
  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  const isActive = (href: string) => {
    if (href === '/app') return location.pathname === '/app'
    return location.pathname.startsWith(href)
  }

  let globalIndex = -1

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 left-4 z-30 w-12 h-12 rounded-xl flex items-center justify-center shadow-lg transition-all hover:scale-110"
        style={{ backgroundColor: 'var(--primary-600)', color: 'white' }}
        title="Menu (Ctrl+K)"
      >
        <Search className="w-5 h-5" />
      </button>

      {/* Palette overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh] command-palette-backdrop"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setIsOpen(false) }}
        >
          <div
            className="w-full max-w-lg rounded-xl shadow-2xl overflow-hidden"
            style={{ backgroundColor: 'var(--surface-card)', border: '1px solid var(--border-default)' }}
          >
            {/* Search input */}
            <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: '1px solid var(--border-default)' }}>
              <Search className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--text-muted)' }} />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Cerca pagina..."
                className="flex-1 bg-transparent outline-none text-sm"
                style={{ color: 'var(--text-primary)' }}
              />
              <kbd className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--surface-elevated)', color: 'var(--text-muted)' }}>ESC</kbd>
            </div>

            {/* Results */}
            <div ref={listRef} className="max-h-80 overflow-y-auto py-2">
              {filteredItems.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                  Nessun risultato per "{query}"
                </p>
              ) : (
                filteredItems.map((group) => (
                  <div key={group.label}>
                    <p className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider"
                      style={{ color: 'var(--text-muted)' }}>
                      {group.label}
                    </p>
                    {group.items.map((item) => {
                      globalIndex++
                      const idx = globalIndex
                      const active = isActive(item.href)
                      return (
                        <button
                          key={item.href}
                          data-index={idx}
                          onClick={() => navigateTo(item.href)}
                          className={clsx(
                            'w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors',
                            idx === selectedIndex && 'command-palette-result selected'
                          )}
                          style={{
                            color: active ? 'var(--primary-600)' : 'var(--text-primary)',
                            backgroundColor: idx === selectedIndex ? 'var(--hover-bg)' : 'transparent',
                            fontWeight: active ? 600 : 400,
                          }}
                        >
                          <item.icon className="w-4 h-4 flex-shrink-0" />
                          <span className="flex-1">{item.name}</span>
                          {item.category && (
                            <span className="text-xs px-1.5 py-0.5 rounded"
                              style={{ backgroundColor: 'var(--surface-elevated)', color: 'var(--text-muted)' }}>
                              {item.category}
                            </span>
                          )}
                          {item.badge && item.badge > 0 && (
                            <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                              {item.badge}
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-4 py-2.5"
              style={{ borderTop: '1px solid var(--border-default)' }}>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-white"
                  style={{ backgroundColor: 'var(--primary-600)' }}>
                  {user?.email?.charAt(0).toUpperCase()}
                </div>
                <span className="text-xs truncate max-w-[200px]" style={{ color: 'var(--text-muted)' }}>
                  {user?.email}
                </span>
              </div>
              <button
                onClick={() => { setIsOpen(false); onLogout() }}
                className="flex items-center gap-1.5 text-xs px-2 py-1 rounded transition-colors hover:text-red-400"
                style={{ color: 'var(--text-muted)' }}
              >
                <LogOut className="w-3.5 h-3.5" />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
